import { Box, Grid, GridItem, Image, Typography } from "@drone-js/core";
import { HStack, VStack } from "apps/commons/components/Stack";
import { replaceCms } from "apps/commons/functions/replaceCms";
import { TextField } from "apps/oneOnboarding/form/TextField";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import type { FunctionComponent } from "react";
import type { FieldPath } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";

type ValidPath = FieldPath<BulkPropertyFormData>;

interface Props {
    fieldPrefix: string;
    propertyKey: string;
    name: string;
    image?: string;
    countryId: number;
    cityName: string;
}

export const PropertyPricingDetail: FunctionComponent<Props> = (props) => {
    const { fieldPrefix, propertyKey, name, image, countryId, cityName } = props;
    const {
        countriesList,
        cmsData: {
            pricing: {
                nightlyRate,
                currencyPerNight,
                pleaseEnterANightlyRateToContinue,
                mustBeMoreThan,
                mustBeLessThan,
            },
        },
    } = useStaticDataAtomValue();

    const staticCountry = countriesList.find((item) => item.id === countryId);

    if (!staticCountry) {
        return null;
    }

    return (
        <VStack backgroundColor="base.inset" gap="16" borderRadius="8" p="16" data-testid="property-pricing-detail">
            <Grid spacing="0">
                <GridItem md={6} sm={12}>
                    <HStack gap="12" alignItems="center" height="100%">
                        <Box overflow="hidden" borderRadius="8" width="48px" height="48px" flexShrink={0}>
                            {image ? (
                                <Image
                                    alt={name}
                                    src={image}
                                    width="48px"
                                    height="48px"
                                    objectFit="cover"
                                    loading="lazy"
                                />
                            ) : (
                                <Box width="48px" height="48px" backgroundColor="base.inset" />
                            )}
                        </Box>
                        <VStack gap="4" overflow="hidden">
                            <Typography variant="body-default-strong" textColor="base.default">
                                {name}
                            </Typography>
                            <Typography variant="caption-default" textColor="base.subtle">
                                {`${cityName}, ${staticCountry.name}`}
                            </Typography>
                        </VStack>
                    </HStack>
                </GridItem>
                <GridItem md={6} sm={12}>
                    <Box pt="16" qM={{ pt: "0" }}>
                        <TextField<BulkPropertyFormData>
                            label={nightlyRate}
                            field={`${fieldPrefix}.rooms.0.basePrice` as ValidPath}
                            type="number"
                            suffix={replaceCms(currencyPerNight, staticCountry.currencyName ?? "")}
                            rules={{
                                min: {
                                    value: staticCountry.minimumRate,
                                    message: replaceCms(
                                        mustBeMoreThan,
                                        staticCountry.minimumRate,
                                        staticCountry.currencyName,
                                    ),
                                },
                                validate: {
                                    notEmpty: (v) => (v as number) > 0 || pleaseEnterANightlyRateToContinue,
                                },
                                ...(staticCountry.maximumRate > 0 && {
                                    max: {
                                        value: staticCountry.maximumRate,
                                        message: replaceCms(
                                            mustBeLessThan,
                                            staticCountry.maximumRate,
                                            staticCountry.currencyName,
                                        ),
                                    },
                                }),
                            }}
                            dataElementName="nightly-rate"
                            dataTestId={`nightly-rate-input-${propertyKey}`}
                        />
                    </Box>
                </GridItem>
            </Grid>
        </VStack>
    );
};
